import { logEvent } from 'firebase/analytics'
import { analytics } from '../config/firebase'

// Analytics event types
export const ANALYTICS_EVENTS = {
  // User actions
  USER_SIGNUP: 'user_signup',
  USER_LOGIN: 'user_login',
  USER_LOGOUT: 'user_logout',
  
  // Subscription events
  TRIAL_STARTED: 'trial_started',
  SUBSCRIPTION_CREATED: 'subscription_created',
  SUBSCRIPTION_CANCELLED: 'subscription_cancelled',
  PAYMENT_SUCCESS: 'payment_success',
  PAYMENT_FAILED: 'payment_failed',
  
  // Match events
  MATCH_CREATED: 'match_created',
  MATCH_JOINED: 'match_joined',
  MATCH_COMPLETED: 'match_completed',
  MATCH_DELETED: 'match_deleted',
  SCORE_UPDATED: 'score_updated',
  
  // Feature usage
  QR_CODE_SCANNED: 'qr_code_scanned',
  COURT_SELECTED: 'court_selected',
  THEME_TOGGLED: 'theme_toggled',
  
  // App events
  APP_INSTALLED: 'app_installed',
  PWA_INSTALL_PROMPT: 'pwa_install_prompt',
  
  // Error events
  ERROR_OCCURRED: 'error_occurred'
} as const

// Analytics service
export class AnalyticsService {
  private static async getAnalytics() {
    return await analytics
  }

  static async logEvent(
    eventName: string, 
    parameters?: Record<string, any>
  ) {
    try {
      const analyticsInstance = await this.getAnalytics()
      if (analyticsInstance) {
        logEvent(analyticsInstance, eventName, parameters)
      }
    } catch (error) {
      console.warn('Analytics event failed:', error)
    }
  }

  // Convenience methods for common events
  static async logUserSignup(method: string = 'email') {
    await this.logEvent(ANALYTICS_EVENTS.USER_SIGNUP, { method })
  }

  static async logUserLogin(method: string = 'email') {
    await this.logEvent(ANALYTICS_EVENTS.USER_LOGIN, { method })
  }

  static async logTrialStarted(planType: string) {
    await this.logEvent(ANALYTICS_EVENTS.TRIAL_STARTED, { plan_type: planType })
  }

  static async logSubscriptionCreated(planType: string, amount: number) {
    await this.logEvent(ANALYTICS_EVENTS.SUBSCRIPTION_CREATED, {
      plan_type: planType,
      amount,
      currency: 'ZAR'
    })
  }

  static async logMatchCreated(sportType: string, courtNumber?: number) {
    await this.logEvent(ANALYTICS_EVENTS.MATCH_CREATED, {
      sport_type: sportType,
      court_number: courtNumber
    })
  }

  static async logScoreUpdated(matchId: string, team1Score: number, team2Score: number) {
    await this.logEvent(ANALYTICS_EVENTS.SCORE_UPDATED, {
      match_id: matchId,
      team1_score: team1Score,
      team2_score: team2Score
    })
  }

  static async logCourtSelected(courtNumber: number) {
    await this.logEvent(ANALYTICS_EVENTS.COURT_SELECTED, {
      court_number: courtNumber
    })
  }

  static async logError(errorType: string, errorMessage: string) {
    await this.logEvent(ANALYTICS_EVENTS.ERROR_OCCURRED, {
      error_type: errorType,
      error_message: errorMessage
    })
  }
}

// Export a simple function for quick usage
export const logAnalyticsEvent = AnalyticsService.logEvent.bind(AnalyticsService)
